if (!customElements.get("product-form-component")) {
  customElements.define(
    "product-form-component",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector("form");
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.submitButton = this.form?.querySelector(".add-to-cart-button");
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector("span")
          : null;
        this.setupSubmitButtonGuard();
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector(
          ".shopify-payment-button",
        );

        if (document.querySelector("cart-drawer") && this.submitButton) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";

        this.initAgreeCondition();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      setupSubmitButtonGuard() {
        if (!this.submitButton) return;
        this.submitButtonDisabled =
          this.submitButton.dataset.canAddToCart === "false" ||
          this.submitButton.hasAttribute("data-mhc-choice-pending");
        const enforceDisabledState = () => {
          if (this.submitButtonDisabled && !this.submitButton.disabled) {
            this.submitButton.disabled = true;
          }
        };
        enforceDisabledState();
        this._submitButtonObserver = new MutationObserver(enforceDisabledState);
        this._submitButtonObserver.observe(this.submitButton, {
          attributes: true,
          attributeFilter: ["disabled"],
        });
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest("product-info");
          const sectionId =
            productInfo?.dataset.section ||
            productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(
            `sticky-atc[data-sticky-section-id="${sectionId}"]`,
          );
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector(
            ".product-form__error-message-wrapper",
          );
          const stickyErrorWrapper = stickyATC.querySelector(
            ".product-form__error-message-wrapper",
          );
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute("hidden");
            const errorMessage = mainErrorWrapper.querySelector(
              ".product-form__error-message",
            )?.textContent;
            stickyErrorWrapper.toggleAttribute(
              "hidden",
              !isVisible || !errorMessage,
            );
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector(
                ".product-form__error-message",
              );
              if (stickyErrorMessage)
                stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ["hidden"],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartError,
            () => {
              syncFromMain();
            },
          );
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add("disabled");

        this.checkbox.addEventListener("change", () => {
          this.buyItNowButton.classList.toggle(
            "disabled",
            !this.checkbox.checked,
          );
        });

        this.form.addEventListener("submit", (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.closest('product-info')?.mhcImportReady === false) {
          this.closest('product-info').mhcImportSelector?.focus();
          return;
        }
        if (this.submitButtonDisabled || this.submitButton.disabled || this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const isTireProduct =
          String(this.dataset.isTire || "").toLowerCase() === "true";
        const requestedQty = Math.max(1, Number(formData.get("quantity")) || 1);

        if (isTireProduct && theme?.tireLimit) {
          this.submitButton.setAttribute("aria-disabled", true);

          theme.tireLimit
            .canAddRequestedQuantity(requestedQty)
            .then((result) => {
              if (!result.allowed) {
                this.handleErrorMessage(theme.tireLimit.getLimitMessage());
                return;
              }

              this.processSubmit(formData);
            })
            .catch((error) => {
              console.warn("[TIRE4QTY] Add-to-cart validation failed", error);
              this.handleErrorMessage(theme.tireLimit.getFetchFailureMessage());
            })
            .finally(() => {
              if (!this.submitButton.classList.contains("loading")) {
                this.submitButton.removeAttribute("aria-disabled");
              }
            });

          return;
        }

        this.processSubmit(formData);
      }

      processSubmit(formData) {
        if (!(formData instanceof FormData)) {
          formData = new FormData(this.form);
        }

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.submitButton
          .querySelector(".loading__spinner")
          .classList.remove("hidden");

        // Also reflect loading state on the sticky add-to-cart button (if present)
        const productInfo = this.closest("product-info");
        const sectionId =
          productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId
          ? document.getElementById(
              `StickyCart-ProductSubmitButton-${sectionId}`,
            )
          : undefined;
        const stickyLoadingSpinner =
          stickyAddButton?.querySelector(".loading__spinner");
        if (stickyAddButton && stickyLoadingSpinner) {
          stickyAddButton.classList.add("loading");
          stickyLoadingSpinner.classList.remove("hidden");
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const properties = document.querySelectorAll(
          '.product-form__input [name^="properties"]',
        );

        properties.forEach((property) => {
          if (property.type === "file") {
            if (
              !property.files ||
              property.files.length === 0 ||
              !property.files[0]
            )
              return;
            formData.append(property.name, property.files[0]);
            return;
          }

          if (property.value == null || property.value === "") return;
          formData.append(property.name, property.value);
        });

        const bundleRoot =
          this.closest("product-info")?.querySelector("[data-bundle-root]") ||
          document.querySelector("[data-bundle-root]");
        const bundleItems =
          window.Bundle?.getSelectedBundleItems?.(bundleRoot) ||
          window.getSelectedBundleItems?.(bundleRoot) ||
          [];

        if (bundleItems.length > 0) {
          this.processBundleSubmit(formData, bundleItems);
          return;
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form-component",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = `${window.routes?.cart_url}`;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form-component",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    if (
                      this.cart &&
                      typeof this.cart.renderContents === "function"
                    ) {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === "function") {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.submitButton
              .querySelector(".loading__spinner")
              .classList.add("hidden");

            // Clear loading state on the sticky add-to-cart button (if present)
            const productInfo = this.closest("product-info");
            const sectionId =
              productInfo?.dataset.section ||
              productInfo?.dataset.originalSection;
            const stickyAddButton = sectionId
              ? document.getElementById(
                  `StickyCart-ProductSubmitButton-${sectionId}`,
                )
              : undefined;
            const stickyLoadingSpinner =
              stickyAddButton?.querySelector(".loading__spinner");
            if (stickyAddButton && stickyLoadingSpinner) {
              stickyAddButton.classList.remove("loading");
              stickyLoadingSpinner.classList.add("hidden");
            }
          });
      }

      processBundleSubmit(formData, bundleItems) {
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.headers["Content-Type"] = "application/json";

        const productId = Number(formData.get("id"));
        const productQuantity = Math.max(
          1,
          Number(formData.get("quantity")) || 1,
        );
        const items = [
          {
            id: productId,
            quantity: productQuantity,
          },
          ...bundleItems,
        ];

        if (this.cart) {
          config.body = JSON.stringify({
            items,
            sections: this.cart
              .getSectionsToRender()
              .map((section) => section.id),
            sections_url: window.location.pathname,
          });
          this.cart.setActiveElement(document.activeElement);
        } else {
          config.body = JSON.stringify({ items });
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form-component",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = `${window.routes?.cart_url}`;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form-component",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    if (
                      this.cart &&
                      typeof this.cart.renderContents === "function"
                    ) {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === "function") {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.submitButton
              .querySelector(".loading__spinner")
              .classList.add("hidden");

            const productInfo = this.closest("product-info");
            const sectionId =
              productInfo?.dataset.section ||
              productInfo?.dataset.originalSection;
            const stickyAddButton = sectionId
              ? document.getElementById(
                  `StickyCart-ProductSubmitButton-${sectionId}`,
                )
              : undefined;
            const stickyLoadingSpinner =
              stickyAddButton?.querySelector(".loading__spinner");
            if (stickyAddButton && stickyLoadingSpinner) {
              stickyAddButton.classList.remove("loading");
              stickyLoadingSpinner.classList.add("hidden");
            }
          });
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
          this._submitButtonObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        disable = disable || this.closest('product-info')?.mhcImportReady === false;
        this.submitButtonDisabled = disable;
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}


